import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { CFormLabel, CFormInput, CButton, CSpinner } from '@coreui/react'
import { Plus, Pencil, Trash, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config'
import { CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle } from '@coreui/react'

const CloudinaryUploader = ({ onUploadSuccess, filesAl = [], onDelete, showStyleButton = false, acceptedFileTypes = '',isFromTaskMgmt=false  }) => {
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [editedFileName, setEditedFileName] = useState('')
  const [previewImage, setPreviewImage] = useState({ visible: false, url: '', name: '' })

  useEffect(() => {
    if (filesAl.length > 0) {
      setFiles((prevFiles) => {
        // Prevent unnecessary re-renders if files are the same
        if (JSON.stringify(prevFiles) === JSON.stringify(filesAl)) {
          return prevFiles
        }

        return filesAl.map((file) => ({
          url: file.url,
          id: file.id,
          type: file.type || 'image',
          fileName: file.fileName || 'Unknown',
          publicId: file.publicId,
          uploaded: true, // Mark as already uploaded
        }))
      })
    }
  }, [filesAl]) // Runs only when `filesAl` updates

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files)
    if (!selectedFiles.length) return

    const newFiles = selectedFiles.map((file) => ({
      file,
      id: file.id,
      fileName: file.name,
      preview: URL.createObjectURL(file),
      uploaded: false,
    }))

    setFiles((prev) => [...prev, ...newFiles])
  }

  // Handle file name editing
  const handleEditClick = (index, currentName) => {
    setEditingIndex(index)
    setEditedFileName(currentName)
  }

  const handleSaveName = (index) => {
    const updatedFiles = [...files]
    updatedFiles[index].fileName = editedFileName
    setFiles(updatedFiles)
    setEditingIndex(null)
  }

  const handleDelete = async (index, fileObj) => {
    const id = fileObj.id
    try {
      const updatedFiles = [...files]
      updatedFiles.splice(index, 1)
      setFiles(updatedFiles)
      await onDelete(id, fileObj)
      toast.success('File deleted successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to delete file', toastConfig)
    }
  }

  // Upload new files to Cloudinary
  const handleUpload = async () => {
    if (!files.some((file) => !file.uploaded)) {
      toast.error('No new files to upload', toastConfig)
      return
    }

    setUploading(true)

    for (let fileObj of files) {
      if (fileObj.uploaded) continue // Skip already uploaded files

      const formData = new FormData()
      formData.append('file', fileObj.file)
      formData.append('upload_preset', 'provio')
      formData.append('folder', 'provio')

      let resourceType = 'auto'
      if (fileObj.file.type.includes('pdf') || fileObj.file.type.includes('msword')) {
        resourceType = 'raw'
      }

      try {
        const response = await axios.post(`https://api.cloudinary.com/v1_1/dxayiclcq/${resourceType}/upload`, formData)

        const uploadedFileUrl = response.data.secure_url
        const publicId = response.data.public_id
        let uploadedFileType = response.data.resource_type
        let assetId = response.data.asset_id

        if (response.data.format === 'pdf' || resourceType === 'raw') {
          uploadedFileType = 'document'
        }

        onUploadSuccess(uploadedFileUrl, uploadedFileType, fileObj.fileName, assetId, publicId)
        toast.success(`Uploaded: ${fileObj.fileName}`, toastConfig)

        setFiles((prev) => prev.map((file) => (file === fileObj ? { ...file, url: uploadedFileUrl, type: uploadedFileType, uploaded: true, id: assetId, publicId } : file)))
        setEditingIndex(null)
        setVisible(false)
      } catch (error) {
        console.error('Upload failed:', error.response?.data || error.message)
        toast.error(`Upload failed for ${fileObj.fileName}`, toastConfig)
      }
    }

    setUploading(false)
  }

  const [visible, setVisible] = useState(false)

  return (
    <div className="d-flex align-items-start gap-3">
      {/* File Selection */}

      <div>
        {showStyleButton ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            onClick={() => setVisible((p) => !p)}
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              background: 'var(--ui-color-primary, #2b7cd3)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Plus color="white" strokeWidth={2} size={40} />
          </div>
        ) : (
          <CButton
            className={isFromTaskMgmt ? "rs_btn-primary" : ""}
            onClick={() => setVisible((p) => !p)}
            style={
              isFromTaskMgmt
                ? {
                    padding: '8px 14px',
                    fontSize: '13px',
                    borderRadius: '10px',
                    lineHeight: 1.2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }
                : undefined
            }
          >
            <Plus size={isFromTaskMgmt ? 18 : 24} />
            {isFromTaskMgmt ? 'Add/Edit Attachments' : 'Add/Edit Images'}
          </CButton>
        )}
      </div>

      <CModal alignment=" " visible={visible} size="lg" scrollable={true} onClose={() => setVisible(false)} aria-labelledby="VerticallyCenteredExample">
        <CModalHeader style={{border: "0"}}>
          <CModalTitle id="VerticallyCenteredExample">File Uploader</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="d-flex align-items-center gap-2">
            <div>
              <CFormLabel
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '20px',
                  background: 'var(--ui-color-primary, #2b7cd3)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <Plus color="white" strokeWidth={2} size={40} />
                <CFormInput multiple className="hidden" type="file" onChange={handleFileSelect} accept={acceptedFileTypes || 'image/*,video/*,.pdf,.doc,.docx'} />
              </CFormLabel>
            </div>

            {/* File Previews */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {filesAl &&
                files.map((file, idx) => {
                  const fileUrl = file.uploaded ? file.url : file.preview || file.url
                  const fileType = file.type || ''
                  const extension = fileUrl.split('.').pop().toLowerCase()
                  return (
                    <div key={idx} className="d-flex flex-column align-items-center">
                      {/* <img
                      src={file.uploaded ? file.url : file.preview}
                      alt="Preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "4px",
                        objectFit: "cover",
                      }}
                    />  */}
                      <div key={idx} className="d-flex flex-column align-items-center text-center">
                        {fileType === 'image' ? (
                          <div
                            onClick={() => setPreviewImage({ visible: true, url: fileUrl, name: file.fileName || 'Image preview' })}
                            style={{
                              position: 'relative',
                              width: '80px',
                              height: '80px',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                            }}
                            className="shadow border"
                          >
                            <img
                              src={fileUrl}
                              alt={file.fileName || 'Preview'}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />

                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                display: 'flex',
                                gap: '6px',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '4px',
                                padding: '2px',
                              }}
                            ></div>
                          </div>
                        ) : fileType === 'video' || ['mp4', 'webm', 'ogg'].includes(extension) ? (
                          <div
                            style={{
                              position: 'relative',
                              width: '80px',
                              height: '80px',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              pointer: 'cursor',
                            }}
                            className="shadow border"
                          >
                            {/* Video */}
                            <video
                              src={fileUrl}
                              controls
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />

                            {/* Action buttons */}
                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                display: 'flex',
                                gap: '6px',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '4px',
                                padding: '2px',
                              }}
                            ></div>
                          </div>
                        ) : fileType === 'document' || ['pdf', 'doc', 'docx', 'csv', 'txt', 'xls', 'xlsx'].includes(extension) ? (
                          <div
                            style={{
                              position: 'relative',
                              width: '80px',
                              height: '80px',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              background: '#f8f9fa',
                            }}
                            className="d-flex align-items-center justify-content-center border shadow"
                          >
                            {/* File Icon & Extension */}
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                textDecoration: 'none',
                                color: 'inherit',
                              }}
                              className="d-flex align-items-center justify-content-center flex-column"
                            >
                              <i className="bi bi-file-earmark-text" style={{ fontSize: '24px' }}></i>
                              <p className="small m-0">{extension.toUpperCase()}</p>
                            </a>

                            {/* Action buttons */}
                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                display: 'flex',
                                gap: '6px',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '4px',
                                padding: '2px',
                              }}
                            ></div>
                          </div>
                        ) : (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: '100px',
                              height: '100px',
                              borderRadius: '4px',
                              background: '#e0e0e0',
                            }}
                            className="d-flex align-items-center justify-content-center border"
                          >
                            <div>
                              <i className="bi bi-file-earmark" style={{ fontSize: '24px' }}></i>
                              <p className="small m-0">File</p>
                            </div>
                          </a>
                        )}
                        <small className="mt-1 text-truncate" style={{ maxWidth: "100px", display: "block" }}>
                          {file.fileName}
                        </small>
                      </div>

                      <div className="d-flex align-items-center gap-1  ">
                        {editingIndex === idx ? (
                          <>
                            <input className="form-control w-100 form-control-sm" value={editedFileName} onChange={(e) => setEditedFileName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSaveName(idx)} />
                            <Check className="cursor-pointer text-success" size={20} onClick={() => handleSaveName(idx)} />
                          </>
                        ) : (
                          <>
                            <span className="text-truncate" style={{ maxWidth: '80px' }}>
                              {file.fileName}
                            </span>
                            {!file.uploaded && <Pencil className="cursor-pointer text-primary" size={15} onClick={() => handleEditClick(idx, file.fileName)} />}
                            <Trash className="cursor-pointer text-danger" size={15} onClick={() => handleDelete(idx, file)} />
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
          
          </div>
        </CModalBody>
        {/* {previewImage.visible && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              backgroundColor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
            }}
            onClick={() => setPreviewImage({ visible: false, url: '', name: '' })}
          >
            <div
              style={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <img
                src={previewImage.url}
                alt={previewImage.name || 'Preview'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
              />
              <button
                type="button"
                onClick={() => setPreviewImage({ visible: false, url: '', name: '' })}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '999px',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 700,
                }}
              >
                ×
              </button>
            </div>
          </div>
        )} */}
       <CModalFooter  style={{
                              borderTop: '1px solid #ced4da',
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '10px',
                              padding: '10px',
                              borderRadius: '0 0 10px 10px',
                          }}>
                            <CButton 
                               onClick={handleUpload} 
                               className={isFromTaskMgmt ? "rs_btn-primary" : ""}  
                               color="primary"
                               style={{
                                 backgroundColor: 'var(--ui-color-primary, #2b7cd3)',
                                 borderColor: 'var(--ui-color-primary, #2b7cd3)',
                                 height: '40px',
                                 display: 'inline-flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                               }}
                             >
            {uploading ? <CSpinner size="sm" /> : 'Upload'}
          </CButton>
          <CButton 
            variant="outline" 
            color="secondary" 
            className={isFromTaskMgmt ? "rs_btn-secondary" : ""} 
            onClick={() => setVisible(false)}
            style={{
              height: '40px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Close
          </CButton>
          
        </CModalFooter>
      </CModal>
    </div>
  )
}

export default CloudinaryUploader
